import React, { useEffect, useState } from "react";
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { Link, useNavigate, useLocation } from "react-router-dom";
import Badge from "@mui/material/Badge";
import { 
  ChatBlackIcon, ChatWhiteIcon, 
  MyProposalBlackIcon, MyProposalWhiteIcon, 
  SettingsBlackIcon, SettingsWhiteIcon, 
  TransactionsBlackIcon, TransactionsWhiteIcon 
} from "../assets/icons/svgs";
import { JandiLogo } from "../assets/logos/svgs";
import Box from '@mui/material/Box';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { api } from "@/utils/api";
import CircularProgress from "@mui/material/CircularProgress";
import { WorkerProfile } from "@/types/userTypes";
import { isWorkerProfileComplete } from "@/utils/workerProfileUtils";

export default function Sidebar({ open, setOpen, variant = 'temporary', width = 280 }: any) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user } = useSelector((state: any) => state.user || {});
  const [loadingItem, setLoadingItem] = React.useState<string | null>(null); // track which item is loading
  const [workerProfileData,setWorkerProfileData] = useState<WorkerProfile>()
  const [isProfileComplete, setIsProfileComplete] = useState<boolean>(true);

  const workerPaymentType =
    workerProfileData?.paymentType ?? user?.paymentType;
  const isStripePayment = workerPaymentType !== 'ATH';
  
  // Build sidebar data: same order for client and provider (1–8)
  const sidebarData = [
    user?.type === 'user' && { title: 'myProposals', url: '/my-proposals', icon: <MyProposalWhiteIcon className="w-6 h-6" />, icon2: <MyProposalBlackIcon className="w-6 h-6" /> },
    user?.type === 'user' && { title: 'requestQuotes', url: '/request-quotes', icon: <MyProposalWhiteIcon className="w-6 h-6" />, icon2: <MyProposalBlackIcon className="w-6 h-6" /> },
    user?.type === 'worker' && { title: 'jobInvitations', url: '/my-jobs', icon: <MyProposalWhiteIcon className="w-6 h-6" />, icon2: <MyProposalBlackIcon className="w-6 h-6" /> },
    user?.type === 'worker' && { title: 'providerRequestQuotes', url: '/provider-request-quotes', icon: <MyProposalWhiteIcon className="w-6 h-6" />, icon2: <MyProposalBlackIcon className="w-6 h-6" /> },
    user?.type === 'worker' && { title: 'providerMyProposals', url: '/provider-proposals', icon: <MyProposalWhiteIcon className="w-6 h-6" />, icon2: <MyProposalBlackIcon className="w-6 h-6" /> },
    { title: 'messages', url: '/chat', icon: <ChatWhiteIcon className="w-6 h-6" />, icon2: <ChatBlackIcon className="w-6 h-6" /> },
    { title: 'my_vouchers', url: '/my-vouchers', icon: <TransactionsWhiteIcon className="w-6 h-6" />, icon2: <TransactionsBlackIcon className="w-6 h-6" /> },
    user?.type === 'user' && { title: 'transactionHistory', url: '/transactions-history', icon: <TransactionsWhiteIcon className="w-6 h-6" />, icon2: <TransactionsBlackIcon className="w-6 h-6" /> },
    user?.type === 'worker' && { title: 'paymentHistory', url: '/payments-history', icon: <TransactionsWhiteIcon className="w-6 h-6" />, icon2: <TransactionsBlackIcon className="w-6 h-6" /> },
    user?.type === 'worker' && isStripePayment && workerProfileData !== undefined && workerProfileData?.isStripeOnBoardingCompleted && { title: 'stripeDashboard', isStripe: true, icon: <TransactionsWhiteIcon className="w-6 h-6" />, icon2: <TransactionsBlackIcon className="w-6 h-6" /> },
    user?.type === 'worker' && isStripePayment && workerProfileData !== undefined && !workerProfileData?.isStripeOnBoardingCompleted && { title: 'stripeOnbaord', isStripe: false, icon: <TransactionsWhiteIcon className="w-6 h-6" />, icon2: <TransactionsBlackIcon className="w-6 h-6" /> },
    { title: 'settings', url: '/settings', icon: <SettingsWhiteIcon className="w-6 h-6" />, icon2: <SettingsBlackIcon className="w-6 h-6" /> },
    { title: 'disputes', url: '/disputes', icon: <TransactionsWhiteIcon className="w-6 h-6" />, icon2: <TransactionsBlackIcon className="w-6 h-6" /> },
    { title: 'help', url: '/help', icon: <TransactionsWhiteIcon className="w-6 h-6" />, icon2: <TransactionsBlackIcon className="w-6 h-6" /> },
    user?.type === 'user' && { title: 'ClientGuidance', url: '/client-faq', icon: <TransactionsWhiteIcon className="w-6 h-6" />, icon2: <TransactionsBlackIcon className="w-6 h-6" /> },
    user?.type === 'worker' && { title: 'ProviderGuidance', url: '/provider-faq', icon: <TransactionsWhiteIcon className="w-6 h-6" />, icon2: <TransactionsBlackIcon className="w-6 h-6" /> },
  ].filter(Boolean);


    useEffect(() => {
  const fetchWorkerProfile = async () => {
    if (user?.type === 'worker') {
      const workerProfile = await api.getMyWorkerProfile();
      if (workerProfile) {
        setWorkerProfileData(workerProfile);
        // Check if profile is complete
        const complete = isWorkerProfileComplete(workerProfile);
        setIsProfileComplete(complete);
      }
    } else {
      // For non-workers, always set to true (no restrictions)
      setIsProfileComplete(true);
    }
  };

  fetchWorkerProfile();
}, [user?.type]);

  // For permanent sidebar, just hide with display: none if not open
  if (variant === 'permanent' && !open) return null;




  return (
    <Drawer
      variant={variant}
      open={variant === 'permanent' ? true : open}
      onClose={variant === 'permanent' ? undefined : () => setOpen(false)}
      anchor="left"
      slotProps={{ paper: { sx: { width, bgcolor: '#fff', boxSizing: 'border-box', borderRight: '1px solid #e5e7eb' } } }}
      sx={{
        width: width,
        flexShrink: 0,
        display: variant === 'permanent' ? { xs: 'none', sm: 'block' } : undefined,
        zIndex: variant === 'permanent' ? 1200 : 1300,
      }}
    >
      <div className="flex items-center justify-between mx-auto py-8 pb-16">
        <Link to="/"><JandiLogo /></Link>
      </div>

      <Box sx={{ px: 1.5 }}>
        <List>
          {sidebarData.map((item, idx) => {
            const isActive = location.pathname === item.url;
            const isLoading = loadingItem === item.title;
            
            // Disable all items except Settings if worker profile is incomplete
            const isDisabled = user?.type === 'worker' && 
                              !isProfileComplete && 
                              item.title !== 'settings';

            const handleClick = async () => {
              // Prevent navigation if disabled
              if (isDisabled) {
                return;
              }
              
              const redirect = item.title === "stripeDashboard" || item.title === "stripeOnbaord";
            
              try {
                if (redirect) {
                  setLoadingItem(item.title);
            
                  // Open a temporary blank tab synchronously — this keeps iOS happy
                  const newWindow = window.open("", "_blank");
            
                  if (workerProfileData?.isStripeOnBoardingCompleted) {
                    const data = await api.getStripeDashboardLink();
                    if (data?.url) {
                      newWindow.location.href = data.url;
                    } else {
                      newWindow.close();
                    }
                  } else {
                    const { status, data } = await api.getAccountLink();
                    if (status === "error") {
                      newWindow.close();
                      return;
                    }
                    const onboardingLink = data?.data;
                    if (onboardingLink) {
                      newWindow.location.href = onboardingLink;
                    } else {
                      newWindow.close();
                    }
                  }
            
                  if (variant !== "permanent") setOpen(false);
                  setLoadingItem(null);
                } else {
                  // Regular navigation for non-Stripe items
                  navigate(item.url);
                  if (variant !== "permanent") setOpen(false);
                }
              } catch (err) {
                console.error("Stripe redirection failed:", err);
                setLoadingItem(null);
              }
            };
            
            // Check if this is Stripe onboarding and not completed
            const isStripeOnboarding = (item.title === "stripeDashboard" || item.title === "stripeOnbaord");
            const isStripeNotCompleted = isStripeOnboarding && !workerProfileData?.isStripeOnBoardingCompleted;
            
            return (
              <ListItemButton
                key={idx}
                onClick={handleClick}
                disabled={isLoading || isDisabled}
                sx={{
                  borderRadius: 2,
                  mb: 1,
                  px: 2,
                  ...(isActive && {
                    bgcolor: '#4285F4',
                    '&:hover': { bgcolor: '#4285F4' },
                  }),
                  ...(isStripeNotCompleted && {
                    bgcolor: '#ef4444',
                    '&:hover': { bgcolor: '#dc2626' },
                  }),
                  '&:hover': { 
                    bgcolor: isActive ? '#4285F4' : isStripeNotCompleted ? '#dc2626' : 'text-gray-500' 
                  },
                  ...(isDisabled && {
                    opacity: 0.5,
                    cursor: 'not-allowed',
                    '&:hover': { bgcolor: 'transparent' },
                  }),
                }}
              >
                <ListItemIcon sx={{ minWidth: 35, position: "relative" }}>
  {item.title === "messages" ? (
    <Badge
      badgeContent={
        user?.unSeenMessageCounter > 99
          ? "99+"
          : user?.unSeenMessageCounter || 0
      }
      color="error"
      invisible={!user?.unSeenMessageCounter}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
    >
      {isLoading ? (
        <CircularProgress size={20} />
      ) : isActive ? item.icon : item.icon2}
    </Badge>
  ) : (
    isLoading ? (
      <CircularProgress size={20} />
    ) : isActive ? item.icon : item.icon2
  )}
</ListItemIcon>

                <ListItemText
                  primary={isLoading ? t("loading") : t(item.title)}
                  sx={{
                    fontFamily: '"Object Sans", system-ui, sans-serif',
                    fontSize: { xs: '15px', sm: '16px' },
                    fontWeight: isActive || isStripeNotCompleted ? 700 : 500,
                    color: isActive ? '#fff' : (isStripeNotCompleted ? '#fff' : '#000'),
                  }}
                />
              </ListItemButton>
            );
          })}
        </List>
      </Box>
    </Drawer>
  );
}
